import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { type Clerk } from '@clerk/clerk-js';

type ClerkLoadOptions = NonNullable<Parameters<Clerk['load']>[0]>;
type ClerkUiConstructor = NonNullable<NonNullable<ClerkLoadOptions['ui']>['ClerkUI']>;

@Injectable({ providedIn: 'root' })
export class ClerkCustomerUiLoaderService {
  private readonly document: Document = inject(DOCUMENT);
  private pending: Promise<ClerkUiConstructor> | null = null;

  public load(key: string): Promise<ClerkUiConstructor> {
    if (this.pending) {
      return this.pending;
    }
    this.pending = this.loadScript(key).catch((error: unknown): never => {
      this.pending = null;
      throw error;
    });
    return this.pending;
  }

  private loadScript(key: string): Promise<ClerkUiConstructor> {
    return new Promise((resolve, reject): void => {
      const win: (Window & { __internal_ClerkUICtor?: ClerkUiConstructor }) | null = this.document.defaultView;
      if (win?.__internal_ClerkUICtor) {
        resolve(win.__internal_ClerkUICtor);
        return;
      }
      const domain: string = atob(key.split('_')[2] ?? '').replace(/\$$/, '');
      if (!/^[a-zA-Z0-9.-]+$/.test(domain) || !domain.includes('.')) {
        reject(new Error('Invalid Clerk publishable key.'));
        return;
      }
      const script: HTMLScriptElement = this.document.createElement('script');
      script.src = `https://${domain}/npm/@clerk/ui@1/dist/ui.browser.js`;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = (): void => {
        if (win?.__internal_ClerkUICtor) {
          resolve(win.__internal_ClerkUICtor);
        } else {
          script.remove();
          reject(new Error('Clerk UI unavailable.'));
        }
      };
      script.onerror = (): void => {
        script.remove();
        reject(new Error('Clerk UI failed to load.'));
      };
      this.document.head.appendChild(script);
    });
  }
}
